import {
  Badge,
  Button,
  Card,
  ProgressBar,
  Section,
  Select,
  useToast,
} from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CardHtml } from '../components/CardHtml';
import { IconTarget } from '../components/Icons';
import { SeletorAssunto } from '../components/SeletorAssunto';
import { Carregando, Erro, TituloPagina, Vazio } from '../components/estado';
import { formatClock, formatNumber, labelDoCaminho } from '../lib/format';
import {
  carregarCard,
  iniciarSimulado,
  responderSimulado,
  useDecks,
  useFinalizarSimulado,
} from '../lib/queries';
import type { SimuladoResult, StudyCard } from '../lib/types';
import { useFocusTimer } from '../lib/useFocusTimer';

const TAMANHOS = [10, 20, 30, 50, 100] as const;

type Fase = 'preparo' | 'rodando' | 'resultado';

export function Simulado(): ReactNode {
  const [params, setParams] = useSearchParams();
  const { data: decks, isPending, error, refetch } = useDecks();
  const finalizar = useFinalizarSimulado();
  const toast = useToast();

  const deckId = Number(params.get('deck') ?? 0);
  const [assunto, setAssunto] = useState<string | null>(null);
  const [tamanho, setTamanho] = useState<number>(20);
  const [seletorAberto, setSeletorAberto] = useState(false);

  const [fase, setFase] = useState<Fase>('preparo');
  const [ids, setIds] = useState<number[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [indice, setIndice] = useState(0);
  const [card, setCard] = useState<StudyCard | null>(null);
  const [mostrarVerso, setMostrarVerso] = useState(false);
  const [acertos, setAcertos] = useState(0);
  const [carregandoCard, setCarregandoCard] = useState(false);
  const [resultado, setResultado] = useState<SimuladoResult | null>(null);
  const [iniciando, setIniciando] = useState(false);

  const cronometro = useFocusTimer('questoes', fase === 'rodando');
  const deck = decks?.find((d) => d.id === deckId) ?? null;

  const escolherDeck = useCallback(
    (id: number) => {
      const proximos = new URLSearchParams(params);
      if (id > 0) proximos.set('deck', String(id));
      else proximos.delete('deck');
      setParams(proximos, { replace: true });
      setAssunto(null);
    },
    [params, setParams],
  );

  async function comecar() {
    if (deckId <= 0) return;
    setIniciando(true);
    try {
      const pool = await iniciarSimulado({ deckId, subtopic: assunto, limite: tamanho });
      if (pool.cardIds.length === 0) {
        toast.error('Não há cards nesta seleção.');
        return;
      }
      setIds(pool.cardIds);
      setSessionId(pool.sessionId);
      setIndice(0);
      setAcertos(0);
      setCard(pool.primeiro);
      setMostrarVerso(false);
      setResultado(null);
      setFase('rodando');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não consegui iniciar o simulado.');
    } finally {
      setIniciando(false);
    }
  }

  const responder = useCallback(
    async (acertou: boolean) => {
      if (sessionId == null || !card) return;

      try {
        await responderSimulado({
          sessionId,
          cardId: card.id,
          acerto: acertou,
        });
      } catch {
        toast.error('Não consegui registrar a resposta.');
        return;
      }

      const proximo = indice + 1;

      if (proximo >= ids.length) {
        cronometro.descarregar();
        try {
          const res = await finalizar.mutateAsync({
            sessionId,
            deckId,
            subtopic: assunto,
          });
          setResultado(res);
        } catch {
          toast.error('Não consegui salvar o resultado.');
        }
        setFase('resultado');
        return;
      }

      setIndice(proximo);
      setMostrarVerso(false);
      setCarregandoCard(true);
      try {
        setCard(await carregarCard(ids[proximo] as number));
      } catch {
        toast.error('Não consegui carregar a próxima questão.');
      } finally {
        setCarregandoCard(false);
      }
    },
    [indice, ids, deckId, assunto, sessionId, card, cronometro, toast, finalizar],
  );

  useEffect(() => {
    if (fase !== 'rodando') return;

    function aoTeclar(e: KeyboardEvent) {
      const alvo = e.target as HTMLElement | null;
      if (alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA')) return;

      if (!mostrarVerso && (e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        setMostrarVerso(true);
        return;
      }
      if (mostrarVerso && (e.key === '1' || e.key === '2')) {
        e.preventDefault();
        void responder(e.key === '2');
      }
    }

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [fase, mostrarVerso, responder]);

  if (isPending) return <Carregando className="py-24" />;
  if (error) {
    return (
      <Section spacing="md">
        <Erro error={error} aoTentarNovamente={() => void refetch()} />
      </Section>
    );
  }

  if (fase === 'resultado' && resultado) {
    return (
      <Section spacing="md" width="reading">
        <ResultadoSimulado
          resultado={resultado}
          tempoSegundos={cronometro.segundos}
          deckNome={deck?.nome ?? ''}
          aoRepetir={() => {
            setFase('preparo');
            setResultado(null);
          }}
        />
      </Section>
    );
  }

  if (fase === 'rodando') {
    const progresso = ((indice + (mostrarVerso ? 1 : 0)) / ids.length) * 100;

    return (
      <Section spacing="sm" width="reading">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="mc-mono mc-eyebrow">Simulado</p>
            <h1 className="mt-1 text-mc-h3">{deck?.nome}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge tone="neutral">{formatClock(cronometro.segundos)}</Badge>
            <Badge tone="accent">
              {indice + 1} de {ids.length}
            </Badge>
          </div>
        </div>

        <ProgressBar value={progresso} label="Progresso" />

        <Card padding="lg" className="mt-6">
          {carregandoCard || !card ? (
            <Carregando />
          ) : (
            <>
              <CardHtml html={card.pergunta || card.frente} className="text-lg" />
              {mostrarVerso ? (
                <>
                  <hr className="mc-rule my-7" />
                  <CardHtml html={card.verso} />
                </>
              ) : null}
            </>
          )}
        </Card>

        <div className="mt-6">
          {mostrarVerso ? (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => void responder(false)}
                className="mc-btn min-h-[56px] rounded-mc-card border border-mc-danger/50 bg-mc-danger/10 font-semibold text-mc-danger transition-colors hover:bg-mc-danger/20"
              >
                Errei <span className="mc-mono text-current opacity-70">(1)</span>
              </button>
              <button
                type="button"
                onClick={() => void responder(true)}
                className="mc-btn min-h-[56px] rounded-mc-card border border-mc-accent/50 bg-mc-accent/10 font-semibold text-mc-accent transition-colors hover:bg-mc-accent/20"
              >
                Acertei <span className="mc-mono text-current opacity-70">(2)</span>
              </button>
            </div>
          ) : (
            <Button size="lg" fullWidth onClick={() => setMostrarVerso(true)}>
              Mostrar resposta
            </Button>
          )}
        </div>

        <p className="mc-mono mt-6 text-center">
          {indice === 0
            ? 'Primeira questão'
            : `Acertos até agora: ${acertos} de ${indice}`}
        </p>
      </Section>
    );
  }

  return (
    <Section spacing="md" width="reading">
      <TituloPagina
        eyebrow="Treino"
        titulo="Simulado"
        descricao="Sorteamos questões da matéria escolhida. As respostas aqui não mexem no agendamento das revisões — é só medição."
      />

      <Card padding="lg" className="flex flex-col gap-6">
        <Select
          label="Matéria"
          value={deckId > 0 ? String(deckId) : ''}
          onChange={(e) => escolherDeck(Number(e.target.value))}
        >
          <option value="">Escolha uma matéria…</option>
          {(decks ?? []).map((d) => (
            <option key={d.id} value={d.id}>
              {d.pacote} — {d.nome} ({formatNumber(d.total)})
            </option>
          ))}
        </Select>

        {deckId > 0 ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setSeletorAberto(true)}>
              {assunto ? 'Trocar assunto' : 'Filtrar por assunto'}
            </Button>
            {assunto ? (
              <>
                <span className="text-sm text-mc-ink2">{labelDoCaminho(assunto)}</span>
                <button
                  type="button"
                  onClick={() => setAssunto(null)}
                  className="mc-mono text-mc-danger underline underline-offset-2"
                >
                  remover
                </button>
              </>
            ) : (
              <span className="text-sm text-mc-ink3">Matéria inteira</span>
            )}
          </div>
        ) : null}

        <fieldset>
          <legend className="mc-mono mb-3">Número de questões</legend>
          <div className="flex flex-wrap gap-2">
            {TAMANHOS.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setTamanho(n)}
                aria-pressed={tamanho === n}
                className={`min-h-[44px] min-w-[64px] rounded-mc-pill border px-4 font-semibold transition-colors ${
                  tamanho === n
                    ? 'border-mc-accent bg-mc-accent/12 text-mc-accent'
                    : 'border-mc-line2 text-mc-ink2 hover:text-mc-ink'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </fieldset>

        <Button
          size="lg"
          fullWidth
          disabled={deckId <= 0}
          loading={iniciando}
          onClick={() => void comecar()}
        >
          <IconTarget className="size-5" />
          Começar simulado
        </Button>

        {deckId <= 0 ? (
          <Vazio
            titulo="Escolha uma matéria acima"
            descricao="Depois é só definir quantas questões você quer responder."
            acao={
              <Link to="/baralhos">
                <Button variant="ghost" size="sm">
                  Ver flashcards
                </Button>
              </Link>
            }
          />
        ) : null}
      </Card>

      {deckId > 0 ? (
        <SeletorAssunto
          aberto={seletorAberto}
          deckId={deckId}
          deckNome={deck?.nome ?? ''}
          selecionado={assunto}
          aoFechar={() => setSeletorAberto(false)}
          aoEscolher={setAssunto}
        />
      ) : null}
    </Section>
  );
}

function ResultadoSimulado({
  resultado,
  tempoSegundos,
  deckNome,
  aoRepetir,
}: {
  resultado: SimuladoResult;
  tempoSegundos: number;
  deckNome: string;
  aoRepetir: () => void;
}): ReactNode {
  const percentual =
    resultado.total > 0 ? Math.round((resultado.acertos / resultado.total) * 100) : 0;

  const avaliacao =
    percentual >= 80
      ? { texto: 'Excelente', tom: 'text-mc-accent' }
      : percentual >= 60
        ? { texto: 'Bom caminho', tom: 'text-mc-accent2' }
        : percentual >= 40
          ? { texto: 'Precisa reforçar', tom: 'text-mc-warning' }
          : { texto: 'Hora de voltar à teoria', tom: 'text-mc-danger' };

  return (
    <Card padding="lg" className="text-center">
      <p className="mc-mono mc-eyebrow mb-4 justify-center">Resultado · {deckNome}</p>

      <p className="font-mc-display text-[clamp(3rem,12vw,5.5rem)] font-semibold leading-none text-mc-accent">
        {resultado.nota.toFixed(1)}
      </p>
      <p className={`mt-2 text-mc-card-title ${avaliacao.tom}`}>{avaliacao.texto}</p>

      <dl className="mx-auto mt-8 grid max-w-md grid-cols-3 gap-3">
        <Numero rotulo="Acertos" valor={String(resultado.acertos)} />
        <Numero rotulo="Erros" valor={String(resultado.total - resultado.acertos)} />
        <Numero rotulo="Tempo" valor={formatClock(tempoSegundos)} />
      </dl>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Button onClick={aoRepetir}>Fazer outro</Button>
        <Link to="/desempenho">
          <Button variant="ghost">Ver evolução</Button>
        </Link>
        <Link to="/baralhos">
          <Button variant="quiet">Voltar aos flashcards</Button>
        </Link>
      </div>
    </Card>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }): ReactNode {
  return (
    <div className="rounded-mc-field border border-mc-line bg-[rgba(0,0,0,0.2)] px-3 py-4">
      <dt className="mc-mono">{rotulo}</dt>
      <dd className="mt-1 font-mc-display text-xl font-semibold tabular-nums text-mc-ink">
        {valor}
      </dd>
    </div>
  );
}
